import { lazy, type ComponentType } from "react";

type Loaded<P> = { default: ComponentType<P> };

/**
 * React.lazy that a server render can wait for. A lazy part suspends the first time an isolate
 * renders it, and a page that suspends streams its content after the shell, inside a hidden
 * element that only script reveals. The edge keeps that copy for every reader, crawlers that run
 * no script among them, so a route's loader awaits `ready()` on the server and the part then
 * renders in place. Browsers still load the part's code on its own, as with React.lazy.
 */
export function readyLazy<P extends object>(load: () => Promise<Loaded<P>>) {
  let loaded: Loaded<P> | undefined;
  let loading: Promise<Loaded<P>> | undefined;
  const ready = () =>
    (loading ??= load().then(
      (module) => (loaded = module),
      (error: unknown) => {
        // A failed load is tried again by the next render rather than kept.
        loading = undefined;
        throw error;
      },
    ));
  const Part = lazy(() => {
    if (!loaded) return ready();
    const part = loaded;
    // oxlint-disable-next-line unicorn/no-thenable -- One that settles as `then` is called is how lazy reads a loaded part without suspending.
    return { then: (resolve: (module: Loaded<P>) => void) => resolve(part) } as Promise<Loaded<P>>;
  });
  return Object.assign(Part, { ready });
}

/** Waits for a lazy part during a server render only; a browser loads it as the page needs it. */
export function readyOnServer(part: { ready: () => Promise<unknown> }) {
  return typeof document === "undefined" ? part.ready() : undefined;
}
